'use client';

import { ComponentProps, Fragment, useEffect, useMemo, useReducer, useRef } from 'react';

import { BottomSheet, IcClose, IcReload, theme, Button } from '@bgzt/ui';

import styles from './FilterBottomSheet.module.css';
import {
  MultiSelection,
  CategorySelection,
  PriceRange,
  SingleButton,
  SingleRound,
  Within,
  Spacing,
} from '~/components';
import { Action } from '~/components/FilterBottomSheet/type';
import { FilterValue, SearchFilter } from '~/apis/search';
import { SearchFilterKey, SearchFilterState } from '~/types';

const filterReducer = (state = FILTER_INITIAL_STATE, action: Action): SearchFilterState => {
  if (action.type === 'reset') {
    return action.payload as SearchFilterState;
  }

  return { ...state, [action.type]: action.payload ?? null };
};

const FILTER_INITIAL_STATE: SearchFilterState = {};

type Props = {
  onSubmit: (state: SearchFilterState) => void;
  data: SearchFilterState; // 초기값
  scrollTarget?: string; // scrollIntoView가 될 대상 -> key 혹은 id 값을 사용한다
  filters: SearchFilter[];
} & Pick<ComponentProps<typeof BottomSheet>, 'isOpen' | 'onClose'>;

/**
 * NOTICE
 * useOverlay를 사용해서 제어하세요!
 * **/

export const FilterBottomSheet = ({ isOpen = false, onClose, onSubmit, data, scrollTarget = '', filters }: Props) => {
  const [state, dispatch] = useReducer(filterReducer, data);

  const targetRef = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const handleScrollIntoView = (target: string) => {
    const targetElement = targetRef?.current?.get(target);

    requestAnimationFrame(() => {
      targetElement?.scrollIntoView({ block: 'center' });
    });
  };

  const generateFilterComponent = (filter: SearchFilter): React.ReactNode => {
    const defaultValue = filter?.values?.find((v: FilterValue) => v?._default)?.value ?? '';

    if (filter.type === 'SINGLE_BUTTON') {
      return (
        <SingleButton
          onChange={(action) => dispatch(action)}
          options={filter?.values ?? []}
          value={String(state[(filter?.key ?? filter?.id) as SearchFilterKey] ?? defaultValue)}
          filterKey={filter?.key ?? filter?.id ?? ''}
          title={filter?.name ?? ''}
          ref={(ref) => {
            if (ref) targetRef.current.set((filter?.key ?? filter?.id) as SearchFilterKey, ref);
          }}
        />
      );
    }

    if (filter.type === 'SINGLE_ROUND') {
      return (
        <SingleRound
          onChange={(action) => dispatch(action)}
          options={filter.values ?? []}
          value={String(state[(filter?.key ?? filter?.id) as SearchFilterKey] ?? defaultValue)}
          filterKey={filter.key ?? ''}
          title={filter.name}
          ref={(ref) => {
            if (ref) targetRef.current.set((filter?.key ?? filter?.id) as SearchFilterKey, ref);
          }}
        />
      );
    }

    if (filter.id === 'price') {
      return (
        <PriceRange
          onChange={(action) => dispatch(action)}
          minPrice={String(state?.minPrice ?? '')}
          maxPrice={String(state?.maxPrice ?? '')}
          ref={(ref) => {
            if (ref) targetRef.current.set('price', ref);
          }}
        />
      );
    }

    if (filter.key === 'categoryId') {
      return (
        <CategorySelection
          onChange={(action) => dispatch(action)}
          options={filter.values ?? []}
          categoryId={String(state[filter.key])}
          filterKey={filter.key ?? ''}
          title={filter.name}
          ref={(ref) => {
            if (ref) targetRef.current.set('categoryId', ref);
          }}
        />
      );
    }

    if (filter.key === 'brandId' || filter.key === 'modelId') {
      return (
        <MultiSelection
          onChange={(action) => dispatch(action)}
          options={filter.values ?? []}
          values={state[filter.key]?.map(String)}
          filterKey={filter.key ?? ''}
          title={filter.name}
          ref={(ref) => {
            if (ref) targetRef.current.set(filter.key as 'brandId' | 'modelId', ref);
          }}
        />
      );
    }

    if (filter.key === 'within') {
      return (
        <Within
          onChange={(action) => dispatch(action)}
          within={state.within ?? ''}
          ref={(ref) => {
            if (ref) targetRef.current.set('within', ref);
          }}
          title={filter.name}
        />
      );
    }

    return null;
  };

  const handleSubmitClick = () => {
    onSubmit({ ...filterWithNull, ...state });
    onClose?.();
    globalThis.window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    dispatch({ type: 'reset', payload: data });
  }, [data]);

  useEffect(() => {
    if (scrollTarget && isOpen) {
      handleScrollIntoView(scrollTarget);
      return;
    }
  }, [isOpen, scrollTarget]);

  const filterWithNull: SearchFilterState = useMemo(() => {
    const result: Record<string, null> = {};

    for (const filter of filters) {
      const { key, id } = filter;
      if (id === 'price') {
        result['minPrice'] = null;
        result['maxPrice'] = null;
        continue;
      }
      result[key ?? id] = null;
    }

    return result;
  }, [filters]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={() => {
        onClose?.();
        dispatch({ type: 'reset', payload: data });
      }}
      // full
      className={styles.container}
    >
      <div className={styles.closeButtonContainer}>
        <button
          className={styles.closeIcon}
          onClick={() => {
            onClose?.();
            dispatch({ type: 'reset', payload: data });
          }}
          data-testid="close-button"
        >
          <IcClose fill={theme.colors['gray-900']} size={20} />
        </button>
      </div>
      <div className={styles.filterContainer}>
        <BottomSheet.Header title="상세필터" />
        {filters.map((filter) => (
          <Fragment key={filter.id}>{generateFilterComponent(filter)}</Fragment>
        ))}
      </div>
      <Spacing height={40} />
      <BottomSheet.Footer showTopGradient className={styles.filterBottomSheetFooter}>
        <Button
          variant="normalGray"
          color="white"
          size="large"
          className={styles.resetButton}
          onClick={() => {
            dispatch({ type: 'reset', payload: FILTER_INITIAL_STATE });
          }}
        >
          <IcReload size={14} fill={theme.colors['gray-900']} />
          선택초기화
        </Button>
        <Button onClick={handleSubmitClick} size="xlarge">
          적용하기
        </Button>
      </BottomSheet.Footer>
    </BottomSheet>
  );
};
